import React, { useMemo, useState } from 'react';
import { ArrowRightIcon, CalendarIcon, MailIcon, MapPinIcon, PhoneIcon, SearchIcon, ShoppingBagIcon, XIcon } from 'lucide-react';
import { CLIENTS } from '../../data/clients';
import { ORDER_STATUS_META } from '../../data/orders';
import { Client } from '../../types';
import { formatPrice } from '../../utils/format';
const RANGES: {
  id: string;
  label: string;
  days: number | null;
}[] = [{
  id: 'all',
  label: 'Todo el tiempo',
  days: null
}, {
  id: '7',
  label: 'Últimos 7 días',
  days: 7
}, {
  id: '30',
  label: 'Últimos 30 días',
  days: 30
}, {
  id: '90',
  label: 'Últimos 90 días',
  days: 90
}];
const TODAY = new Date('2026-08-06');
export function ClientsSection() {
  const [query, setQuery] = useState('');
  const [range, setRange] = useState('all');
  const [selected, setSelected] = useState<Client | null>(null);
  const clients = useMemo(() => {
    const days = RANGES.find(r => r.id === range)?.days ?? null;
    return CLIENTS.filter(client => {
      if (query && !`${client.name} ${client.email} ${client.phone}`.toLowerCase().includes(query.toLowerCase())) {
        return false;
      }
      if (days) {
        const diff = (TODAY.getTime() - new Date(client.lastPurchaseIso).getTime()) / 86400000;
        if (diff > days) return false;
      }
      return true;
    });
  }, [query, range]);
  const totalSpent = clients.reduce((sum, c) => sum + c.spent, 0);
  return <>
      <section className="border border-white/8 bg-ink-700" data-id="element-209" data-mp="1gz2ogmymgciu">
        <div className="flex flex-col gap-4 border-b border-white/8 p-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-210" data-mp="1gwgi2qngw327">
          <div data-id="element-211" data-mp="1qwqsk3i4awdo">
            <h2 className="font-display text-2xl uppercase tracking-wide text-white" data-id="element-212" data-mp="1qw7a8lok25z4">
              Clientes
            </h2>
            <p className="mt-1 text-xs text-white/40" data-id="element-213" data-mp="135br0cy60j3h">
              {clients.length} clientes · {formatPrice(totalSpent)} facturados
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-214" data-mp="2yaulbed7js1j">
            <div className="relative" data-id="element-215" data-mp="3395s1g87hmfj">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" data-id="element-216" data-mp="33849kqov1xns" />
              <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar por nombre, email o teléfono..." aria-label="Buscar cliente" className="w-full border border-white/10 bg-ink-900 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-pink-500 focus:outline-none sm:w-72" data-id="element-217" data-mp="380nvcaxpbz3o" />
            </div>
            <div className="relative" data-id="element-218" data-mp="2tc169hi9vsz9">
              <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" data-id="element-219" data-mp="2t8vfzyxwql1y" />
              <select value={range} onChange={e => setRange(e.target.value)} aria-label="Filtrar por última compra" className="w-full border border-white/10 bg-ink-900 py-2.5 pl-9 pr-3 text-sm text-white focus:border-pink-500 focus:outline-none sm:w-48" data-id="element-220" data-mp="8kqwhdrcem86">
                {RANGES.map(r => <option key={r.id} value={r.id} data-id="element-221" data-mp="8wrst5dvn6oz">
                    {r.label}
                  </option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-222" data-mp="3w4vheivghsax">
          <table className="w-full min-w-[940px] text-left text-sm" data-id="element-223" data-mp="3r3y4olt7tqgt">
            <thead data-id="element-224" data-mp="3r6u0a3hlc323">
              <tr className="border-b border-white/8 text-[10px] uppercase tracking-[0.16em] text-white/40" data-id="element-225" data-mp="3m54m4ez2n99s">
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-226" data-mp="3i4aylmpjjwhf">
                  Avatar
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-227" data-mp="1kczznj0cd46g">
                  Nombre
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-228" data-mp="1gdr4c9pnky8s">
                  Email
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-229" data-mp="1ngfjvizna209">
                  Teléfono
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-230" data-mp="1scd2xx2almgq">
                  Pedidos
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-231" data-mp="1igjjdtq5ospg">
                  Total gastado
                </th>
                <th scope="col" className="px-5 py-3 font-semibold" data-id="element-232" data-mp="14ov4xjsfps5b">
                  Última compra
                </th>
                <th scope="col" className="px-5 py-3 text-right font-semibold" data-id="element-233" data-mp="19gmxfzv5i7y3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody data-id="element-234" data-mp="zpijn3h7ot4u">
              {clients.map(client => <tr key={client.id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]" data-id="element-235" data-mp="znz5bif48s5p">
                  <td className="px-5 py-3" data-id="element-236" data-mp="qou23ysm0an7">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-500/15 font-display text-sm tracking-wider text-pink-400" aria-hidden data-id="element-237" data-mp="73brgb6jmijt">
                      {client.initials}
                    </span>
                  </td>
                  <td className="px-5 py-3" data-id="element-238" data-mp="c1lnsp407p70">
                    <p className="font-semibold text-white" data-id="element-239" data-mp="c4rbq10wcl15">{client.name}</p>
                    <p className="text-xs text-white/35" data-id="element-240" data-mp="q0v55s3y4pck">{client.city}</p>
                  </td>
                  <td className="px-5 py-3 text-white/60" data-id="element-241" data-mp="l2tkx3usajzi">{client.email}</td>
                  <td className="px-5 py-3 text-white/60" data-id="element-242" data-mp="zr7lt98n3ydf">{client.phone}</td>
                  <td className="px-5 py-3" data-id="element-243" data-mp="usml78ph7cog">
                    <span className="border border-white/12 bg-ink-600 px-2.5 py-1 text-xs font-semibold text-white" data-id="element-244" data-mp="uto94drlbtnl">
                      {client.orders}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-semibold text-white" data-id="element-245" data-mp="13vns3bm617t6">
                    {formatPrice(client.spent)}
                  </td>
                  <td className="px-5 py-3 text-white/60" data-id="element-246" data-mp="6f9kcvzbvy69">
                    {client.lastPurchase}
                  </td>
                  <td className="px-5 py-3" data-id="element-247" data-mp="ag1vjurrgd56">
                    <div className="flex justify-end" data-id="element-248" data-mp="af08erxh0073">
                      <button type="button" onClick={() => setSelected(client)} className="group flex items-center gap-2 border border-white/15 px-3.5 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-white/70 transition-colors hover:border-pink-500 hover:text-white" data-id="element-249" data-mp="f8uyqe7kzdf5">
                        Ver detalle
                        <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-250" data-mp="y5e4ctj6ej6y" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {clients.length === 0 && <div className="px-6 py-16 text-center" data-id="element-251" data-mp="2sfua1gc5lygg">
            <h3 className="font-display text-2xl uppercase text-white" data-id="element-252" data-mp="2nnawgjdgv3bn">
              Sin clientes
            </h3>
            <p className="mx-auto mt-2 max-w-sm text-sm text-white/40" data-id="element-253" data-mp="2iprlbsc2f6nx">
              No hay clientes que coincidan con la búsqueda o el período elegido.
            </p>
          </div>}
      </section>

      {/* DETAIL PANEL */}
      {selected && <div className="fixed inset-0 z-[70] flex justify-end" role="dialog" aria-modal="true" aria-labelledby="client-panel-title" data-id="element-254" data-mp="36easg4jdpdef">
          <div className="absolute inset-0 bg-black/75" onClick={() => setSelected(null)} data-id="element-255" data-mp="32etl15ke666m" />
          <div className="relative flex h-full w-full max-w-lg flex-col overflow-y-auto border-l border-white/10 bg-ink-800" data-id="element-256" data-mp="1qxadx23oxj2s">
            <div className="flex items-start justify-between gap-4 border-b border-white/8 p-6" data-id="element-257" data-mp="1lxfkorwg0hek">
              <div className="flex items-center gap-4" data-id="element-258" data-mp="3lzniqn66zo4l">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-pink-500 font-display text-lg tracking-wider text-white" aria-hidden data-id="element-259" data-mp="3m32w9lk7wp3w">
                  {selected.initials}
                </span>
                <div data-id="element-260" data-mp="3h3q2shyucjsc">
                  <h2 id="client-panel-title" className="font-display text-2xl uppercase leading-none tracking-wide text-white" data-id="element-261" data-mp="8lu0jvae41cd">
                    {selected.name}
                  </h2>
                  <p className="mt-1.5 text-xs text-white/40" data-id="element-262" data-mp="3v0lp3c43s7qc">
                    Cliente desde {selected.joined}
                  </p>
                </div>
              </div>
              <button type="button" onClick={() => setSelected(null)} aria-label="Cerrar detalle" className="p-1.5 text-white/50 transition-colors hover:text-white" data-id="element-263" data-mp="rkej2yy7tyee">
                <XIcon className="h-5 w-5" data-id="element-264" data-mp="2gzpan94193ad" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 border-b border-white/8 p-6" data-id="element-265" data-mp="2c277jzcp5iaa">
              {[{
            k: String(selected.orders),
            v: 'Pedidos'
          }, {
            k: formatPrice(selected.spent),
            v: 'Gastado'
          }, {
            k: formatPrice(Math.round(selected.spent / selected.orders)),
            v: 'Ticket prom.'
          }].map(stat => <div key={stat.v} className="border border-white/8 bg-ink-700 p-4" data-id="element-266" data-mp="1t5xpl0yoc7l0">
                  <p className="font-display text-xl leading-none text-pink-500" data-id="element-267" data-mp="274xco46jgef2">
                    {stat.k}
                  </p>
                  <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-white/40" data-id="element-268" data-mp="3iispv7dvud3n">
                    {stat.v}
                  </p>
                </div>)}
            </div>

            <dl className="space-y-4 border-b border-white/8 p-6 text-sm" data-id="element-269" data-mp="evulednynlaw">
              <div className="flex items-center gap-3" data-id="element-270" data-mp="exyowuhy6ian">
                <MailIcon className="h-4 w-4 shrink-0 text-pink-500" data-id="element-271" data-mp="9y2n91wq32y9" />
                <dt className="sr-only" data-id="element-272" data-mp="55sxpmzis4wi">Email</dt>
                <dd className="text-white/70" data-id="element-273" data-mp="54hvhrlq357i">{selected.email}</dd>
              </div>
              <div className="flex items-center gap-3" data-id="element-274" data-mp="13xuue1xxjgm">
                <PhoneIcon className="h-4 w-4 shrink-0 text-pink-500" data-id="element-275" data-mp="3sar0pv7oi6rh" />
                <dt className="sr-only" data-id="element-276" data-mp="3s8nxualfgj5c">Teléfono</dt>
                <dd className="text-white/70" data-id="element-277" data-mp="3naw5jfgvev0m">{selected.phone}</dd>
              </div>
              <div className="flex items-center gap-3" data-id="element-278" data-mp="3ic2sfo5hx60z">
                <MapPinIcon className="h-4 w-4 shrink-0 text-pink-500" data-id="element-279" data-mp="3iexf7mbzf2xk" />
                <dt className="sr-only" data-id="element-280" data-mp="3ectyojagh85f">Dirección</dt>
                <dd className="text-white/70" data-id="element-281" data-mp="1gl7x0xf7b8us">{selected.city}</dd>
              </div>
            </dl>

            <div className="p-6" data-id="element-282" data-mp="1cjcujpljapff">
              <h3 className="flex items-center gap-2 font-display text-lg uppercase tracking-[0.16em] text-white" data-id="element-283" data-mp="1cl7fh3wt2hji">
                <ShoppingBagIcon className="h-4 w-4 text-pink-500" data-id="element-284" data-mp="2uh97d06ieobj" />
                Historial de pedidos
              </h3>
              <ul className="mt-5 space-y-3" data-id="element-285" data-mp="2ze9frhqu035t">
                {selected.history.map(order => {
              const meta = ORDER_STATUS_META[order.status];
              return <li key={order.id} className="border border-white/8 bg-ink-700 p-4" data-id="element-286" data-mp="38aqq4irdfx0i">
                      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-287" data-mp="2girphj3ac61j">
                        <span className="font-display text-lg leading-none text-white" data-id="element-288" data-mp="2kia6w4vwdyzm">
                          {order.id}
                        </span>
                        <span className={`border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${meta.className}`} data-id="element-289" data-mp="2pg20d3c6xt5x">
                          {meta.label}
                        </span>
                      </div>
                      <p className="mt-2.5 text-sm text-white/55" data-id="element-290" data-mp="20tir84z99vjc">{order.items}</p>
                      <div className="mt-3 flex items-center justify-between text-xs" data-id="element-291" data-mp="1wt74mmnc1l58">
                        <span className="text-white/35" data-id="element-292" data-mp="2as6ufxml062z">{order.date}</span>
                        <span className="font-semibold text-white" data-id="element-293" data-mp="25tx0trgi9djh">
                          {formatPrice(order.total)}
                        </span>
                      </div>
                    </li>;
            })}
              </ul>
            </div>

            <div className="mt-auto flex gap-3 border-t border-white/8 p-6" data-id="element-294" data-mp="1rungqgp6hza8">
              <a href={`mailto:${selected.email}`} className="flex-1 bg-pink-500 py-3 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-295" data-mp="1rwqh9unyjat7">
                Contactar
              </a>
              <button type="button" onClick={() => setSelected(null)} className="flex-1 border border-white/20 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-white/70 transition-colors hover:border-white hover:text-white" data-id="element-296" data-mp="uezun4s6w7ow">
                Cerrar
              </button>
            </div>
          </div>
        </div>}
    </>;
}